import React, { useState, useEffect } from 'react';
import axiosInstance from '../../apis/axiosConfig';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaCar, FaArrowLeft, FaMapMarkerAlt, FaDollarSign, FaTachometerAlt, FaCalendarAlt, FaUser, FaInfoCircle } from 'react-icons/fa';
import '../../pages_premium.css';
import './AddCar.css';

function AddCar() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeField, setActiveField] = useState(null);
    const [formData, setFormData] = useState({
        owner_id: '',
        make: '',
        model: '',
        year: '',
        price: '',
        mileage: '',
        location: '',
        description: '',
        car_category: 'per_day',
        status: 'available'
    });

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await axiosInstance.get('/admin/users');
                if (Array.isArray(res.data)) {
                    setUsers(res.data);
                } else {
                    setUsers([]);
                }
            } catch (error) {
                console.error('Error fetching users:', error);
                toast.error('Failed to load users');
                setUsers([]);
            }
        };
        fetchUsers();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axiosInstance.post('/cars', formData);
            toast.success('🚗 Car listed successfully!');
            navigate('/cars');
        } catch (error) {
            console.error('Error adding car:', error);
            toast.error(error.response?.data?.message || 'Failed to add car');
        } finally {
            setLoading(false);
        }
    };

    const isComplete = formData.owner_id && formData.make && formData.model && formData.year && formData.price && formData.mileage && formData.location;
    const completionPercent = [formData.owner_id, formData.make, formData.model, formData.year, formData.price, formData.mileage, formData.location, formData.description]
        .filter(Boolean).length / 8 * 100;

    return (
        <div className="page-premium-wrapper">
            <div className="page-premium-content">

                {/* Hero Header — same as other pages */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaCar size={28} />
                        </div>
                        <div>
                            <h1 className="ph-title">Add New Car</h1>
                            <p className="ph-subtitle">List a new car for rental on the platform</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{Math.round(completionPercent)}%</span>
                            <span className="count-lbl">Completed</span>
                        </div>
                    </div>
                </div>

                {/* Back button */}
                <div className="mb-3">
                    <button className="ac-back-btn" onClick={() => navigate('/cars')}>
                        <FaArrowLeft /> Back to List
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="ac-progress-bar-wrap">
                    <div className="ac-progress-bar-track">
                        <div className="ac-progress-bar-fill" style={{ width: `${completionPercent}%` }}></div>
                    </div>
                    <span className="ac-progress-text">Form {Math.round(completionPercent)}% filled</span>
                </div>

                {/* Main Form Card — full width */}
                <div className="ac-form-card">
                    <form onSubmit={handleSubmit}>

                        {/* Section: Owner */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon">
                                    <FaUser />
                                </div>
                                <div>
                                    <h2>Owner Information</h2>
                                    <p>Select which user owns this car listing</p>
                                </div>
                            </div>
                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ac-field-full ${activeField === 'owner_id' ? 'ac-field-active' : ''} ${formData.owner_id ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="owner_id">Select Owner <span className="ac-required">*</span></label>
                                    <select
                                        id="owner_id"
                                        name="owner_id"
                                        value={formData.owner_id}
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('owner_id')}
                                        onBlur={() => setActiveField(null)}
                                        required
                                    >
                                        <option value="">-- Choose an owner --</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.name} ({u.email}) · {u.role}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="ac-field-hint"><FaInfoCircle size={11} /> As an admin, you can assign this car to any registered user</div>
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* Section: Car Details */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon">
                                    <FaCar />
                                </div>
                                <div>
                                    <h2>Car Details</h2>
                                    <p>Brand, model and manufacturing year</p>
                                </div>
                            </div>
                            <div className="ac-field-group">
                                <div className="ac-field-wrap w-100" style={{ gridColumn: '1 / -1' }}>
                                    <label htmlFor="car_category">
                                        🇦🇺 Vehicle Category (Australia Standard) <span className="ac-required">*</span>
                                    </label>
                                    <select
                                        id="car_category"
                                        name="car_category"
                                        value={formData.car_category || 'per_day'}
                                        onChange={handleChange}
                                        required
                                        style={{ fontSize: '13px', fontWeight: 600 }}
                                    >
                                        <option value="per_day">🚗 Per Day / Short-Term Rental (Flexible 1-30+ Days)</option>
                                        <option value="uber_eats">🍔 Uber Eats / Delivery (Food & Parcels • Min 1 Month Rental)</option>
                                        <option value="uber_taxi">🚕 Uber / Taxi (Rideshare Transport • Min 1 Month Rental)</option>
                                        <option value="pickup_van">🛻 Ute / Pickup / Van (Tradies & Cargo • Min 1 Month Rental)</option>
                                    </select>
                                    <div className="ac-field-hint" style={{ color: formData.car_category !== 'per_day' ? '#b45309' : '#0369a1' }}>
                                        <FaInfoCircle size={11} /> 
                                        {formData.car_category && formData.car_category !== 'per_day'
                                            ? 'Australia Rule: Commercial rideshare, delivery, and ute/van listings require a minimum 1-month rental contract.'
                                            : 'Flexible: Can be rented per day (1, 10, 20 days) or any custom period.'}
                                    </div>
                                </div>

                                <div className={`ac-field-wrap ${activeField === 'make' ? 'ac-field-active' : ''} ${formData.make ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="make">Make <span className="ac-required">*</span></label>
                                    <input
                                        type="text"
                                        id="make"
                                        name="make"
                                        value={formData.make}
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('make')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="e.g. Toyota"
                                        required
                                    />
                                </div>

                                <div className={`ac-field-wrap ${activeField === 'model' ? 'ac-field-active' : ''} ${formData.model ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="model">Model <span className="ac-required">*</span></label>
                                    <input
                                        type="text"
                                        id="model"
                                        name="model"
                                        value={formData.model}
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('model')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="e.g. Corolla"
                                        required
                                    />
                                </div>

                                <div className={`ac-field-wrap ${activeField === 'year' ? 'ac-field-active' : ''} ${formData.year ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="year"><FaCalendarAlt size={11} style={{ marginRight: 4 }} /> Year <span className="ac-required">*</span></label>
                                    <input
                                        type="number"
                                        id="year"
                                        name="year"
                                        value={formData.year}
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('year')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="e.g. 2022"
                                        min="1990"
                                        max="2030"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* Section: Pricing & Location */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon ac-section-icon-green">
                                    <FaDollarSign />
                                </div>
                                <div>
                                    <h2>Pricing &amp; Location</h2>
                                    <p>Set rental price, mileage and pickup city</p>
                                </div>
                            </div>
                            <div className="ac-field-group ac-field-group-3">
                                <div className={`ac-field-wrap ${activeField === 'price' ? 'ac-field-active' : ''} ${formData.price ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="price">Price per Day ($) <span className="ac-required">*</span></label>
                                    <div className="ac-input-with-icon">
                                        <span className="ac-input-prefix">$</span>
                                        <input
                                            type="number"
                                            id="price"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleChange}
                                            onFocus={() => setActiveField('price')}
                                            onBlur={() => setActiveField(null)}
                                            placeholder="85"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className={`ac-field-wrap ${activeField === 'mileage' ? 'ac-field-active' : ''} ${formData.mileage ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="mileage"><FaTachometerAlt size={11} style={{ marginRight: 4 }} /> Mileage (km) <span className="ac-required">*</span></label>
                                    <input
                                        type="number"
                                        id="mileage"
                                        name="mileage"
                                        value={formData.mileage}
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('mileage')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="15000"
                                        required
                                    />
                                </div>

                                <div className={`ac-field-wrap ${activeField === 'location' ? 'ac-field-active' : ''} ${formData.location ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="location"><FaMapMarkerAlt size={11} style={{ marginRight: 4 }} /> Location <span className="ac-required">*</span></label>
                                    <div className="ac-input-with-icon">
                                        <span className="ac-input-prefix"><FaMapMarkerAlt size={12} /></span>
                                        <input
                                            type="text"
                                            id="location"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            onFocus={() => setActiveField('location')}
                                            onBlur={() => setActiveField(null)}
                                            placeholder="e.g. Sydney CBD, NSW"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* Section: Description */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon ac-section-icon-purple">
                                    <FaInfoCircle />
                                </div>
                                <div>
                                    <h2>Additional Info</h2>
                                    <p>A brief description to attract renters</p>
                                </div>
                            </div>
                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ac-field-full ${activeField === 'description' ? 'ac-field-active' : ''} ${formData.description ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="description">Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        rows="4"
                                        value={formData.description}
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('description')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="Describe the car features, condition, fuel type, amenities..."
                                    ></textarea>
                                    <div className="ac-char-count">{formData.description.length} characters</div>
                                </div>
                            </div>
                        </div>

                        {/* Submit Footer */}
                        <div className="ac-form-footer">
                            <button type="button" className="ac-cancel-btn" onClick={() => navigate('/cars')}>
                                Cancel
                            </button>
                            <button type="submit" className={`ac-submit-btn ${isComplete ? 'ac-submit-ready' : ''}`} disabled={loading}>
                                {loading ? (
                                    <span className="ac-loading-inner">
                                        <span className="ac-spinner"></span> Listing Car...
                                    </span>
                                ) : (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FaCar /> List This Car
                                    </span>
                                )}
                            </button>
                        </div>

                    </form>
                </div>

            </div>
        </div>
    );
}

export default AddCar;
